import { pick, unwrapData, unwrapList } from '../utils/moduleHelpers';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toDescList = (descriptions) => {
  if (!Array.isArray(descriptions)) return [];
  return descriptions
    .map((d) => {
      if (typeof d === 'string') return d;
      return d?.description || d?.Description || d?.text || '';
    })
    .filter(Boolean);
};

export const normalizeLoyaltyEvents = (payload) => {
  const raw = unwrapData({ data: payload }) ?? payload;
  const source = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.events)
      ? raw.events
      : Array.isArray(raw?.Events)
        ? raw.Events
        : unwrapList({ data: raw }) || [];

  return (Array.isArray(source) ? source : []).map((item, index) => {
    const eventPoints = Array.isArray(item.eventPoints)
      ? item.eventPoints
      : Array.isArray(item.EventPoints)
        ? item.EventPoints
        : [];

    const nestedPoints = eventPoints
      .map((ep) => toNum(ep?.points ?? ep?.Points ?? ep?.loyaltyPoints))
      .filter((n) => n > 0);

    const points = toNum(
      item.points ??
        item.Points ??
        item.loyaltyPoints ??
        item.LoyaltyPoints ??
        (nestedPoints.length ? Math.max(...nestedPoints) : 0)
    );

    return {
      id:
        item.affiliateLoyaltyEventId ??
        item.AffiliateLoyaltyEventId ??
        item.id ??
        index,
      name:
        item.loyaltyEventName ||
        item.LoyaltyEventName ||
        item.eventName ||
        item.EventName ||
        item.name ||
        item.Name ||
        `Event ${index + 1}`,
      description:
        item.description ||
        item.Description ||
        item.subtitle ||
        item.Subtitle ||
        '',
      points,
      eventPoints,
    };
  });
};

export const normalizeLoyaltyTiers = (payload) => {
  const list = unwrapList({ data: payload });
  const source = Array.isArray(payload) ? payload : list;
  return (Array.isArray(source) ? source : [])
    .map((item, index) => {
      const nested =
        item.descriptions ??
        item.Descriptions ??
        item.affiliateLoyaltyTierDescriptions ??
        item.AffiliateLoyaltyTierDescriptions ??
        [];
      return {
        id: item.affiliateLoyaltyTierId ?? item.AffiliateLoyaltyTierId ?? item.id ?? index + 1,
        name: item.tierName || item.TierName || item.name || item.Name || `Tier ${index + 1}`,
        benefits: toDescList(nested),
        required: toNum(
          item.requiredPoints ??
            item.RequiredPoints ??
            item.tierPoints ??
            item.TierPoints ??
            item.points ??
            0
        ),
        createdDateTime: item.createdDateTime || item.CreatedDateTime || '',
      };
    })
    .sort((a, b) => a.required - b.required || Number(a.id) - Number(b.id));
};

const buildLoyaltyDescription = (eventName, points) => {
  const name = String(eventName || '').trim();
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  const abs = Math.abs(Number(points) || 0);
  const ptsLabel = abs.toLocaleString('en-US');

  if (points < 0 || key.includes('redeem')) {
    return `Redeemed ${ptsLabel} loyalty points`;
  }
  if (key.includes('refer')) return 'Successful referral bonus';
  if (key.includes('promot')) return 'Promotion campaign bonus';
  if (key.includes('deposit')) return `Deposit loyalty credit (+${ptsLabel} PTS)`;
  if (key.includes('trad')) return `Loyalty points for trading activity (+${ptsLabel} PTS)`;
  if (key.includes('creat') || key.includes('verif') || key.includes('account')) {
    return 'Account verification completed';
  }
  if (key.includes('transfer')) return `Points transfer (+${ptsLabel} PTS)`;
  if (name) return `${name} — ${points >= 0 ? '+' : ''}${ptsLabel} PTS`;
  return points >= 0 ? `Earned ${ptsLabel} loyalty points` : `Redeemed ${ptsLabel} loyalty points`;
};

const resolveActivityLabel = (eventName, points) => {
  const name = String(eventName || '').trim();
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (points < 0) return 'Redemption';
  if (key.includes('refer')) return 'Referral';
  if (key.includes('promot')) return 'Promotion';
  if (key.includes('deposit')) return 'Deposit';
  if (key.includes('trad')) return 'Trading';
  if (key.includes('creat') || key.includes('verif') || key.includes('account')) return 'Verification';
  if (key.includes('transfer')) return 'Transfer';
  if (name) {
    return name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return points >= 0 ? 'Earned' : 'Redemption';
};

export const normalizeLoyaltyHistory = (payload) => {
  const data = unwrapData({ data: payload }) ?? payload ?? {};

  // API shape: { totalCount, pageNumber, pageSize, items: [...] }
  // Do NOT use `unwrapList(...) || ...` — empty [] is truthy and blocks later sources.
  let list = [];
  if (Array.isArray(data?.items)) list = data.items;
  else if (Array.isArray(data?.Items)) list = data.Items;
  else if (Array.isArray(data?.history)) list = data.history;
  else if (Array.isArray(data?.History)) list = data.History;
  else if (Array.isArray(data)) list = data;
  else {
    const fallback = unwrapList({ data });
    if (Array.isArray(fallback)) list = fallback;
  }

  const rows = list.map((item, index) => {
    const points = toNum(
      item.loyaltyPoints ??
        item.LoyaltyPoints ??
        item.points ??
        item.Points ??
        item.pointsDelta ??
        item.amount
    );
    const eventName =
      item.loyaltyEventName || item.LoyaltyEventName || item.eventName || item.EventName || '';
    const rawDescription =
      item.description || item.Description || item.remarks || item.Remarks || '';
    const activity = resolveActivityLabel(eventName || rawDescription, points);
    const description =
      rawDescription ||
      buildLoyaltyDescription(eventName || activity, points);

    return {
      id: item.affiliateLoyaltyPointsHistoryId ?? item.AffiliateLoyaltyPointsHistoryId ?? item.id ?? index,
      date: item.createdDateTime || item.CreatedDateTime || item.date || item.createdAt || '',
      activity,
      description,
      type: points < 0 ? 'Redeemed' : 'Earned',
      points,
      loyaltyEventName: eventName,
      createdDateTime: item.createdDateTime || item.CreatedDateTime || '',
    };
  });

  const pagination = data.pagination || data.Pagination || {};
  const totalCount = toNum(
    pagination.totalCount ?? data.totalCount ?? data.TotalCount ?? rows.length
  );
  const pageSize = toNum(pagination.pageSize ?? data.pageSize ?? data.PageSize ?? rows.length) || 10;
  const pageNumber = toNum(pagination.pageNumber ?? data.pageNumber ?? data.PageNumber ?? 1) || 1;

  return {
    rows,
    pagination: {
      totalCount,
      pageNumber,
      pageSize,
      totalPages:
        toNum(pagination.totalPages ?? data.totalPages) ||
        Math.max(1, Math.ceil(totalCount / pageSize) || 1),
    },
  };
};

/**
 * Aggregate earned loyalty history into Points Breakdown slices.
 * PTS per category = sum of positive history points for that event (user actually earned).
 * Percentage = categoryPts / totalEarned * 100.
 */
export const buildPointsBreakdownFromHistory = (historyRows = []) => {
  const map = new Map();

  (Array.isArray(historyRows) ? historyRows : []).forEach((row) => {
    const pts = toNum(row?.points ?? row?.loyaltyPoints ?? row?.LoyaltyPoints);
    if (!(pts > 0)) return;

    // Prefer normalized activity label (Trading, Deposits, …) for chart grouping
    const label =
      String(
        row.activity ||
          resolveActivityLabel(row.loyaltyEventName || row.LoyaltyEventName || '', pts) ||
          row.loyaltyEventName ||
          row.LoyaltyEventName ||
          'Other'
      ).trim() || 'Other';

    const prev = map.get(label) || 0;
    map.set(label, prev + pts);
  });

  const slices = Array.from(map.entries())
    .map(([name, points]) => ({ name, points }))
    .sort((a, b) => b.points - a.points);

  const total = slices.reduce((sum, s) => sum + s.points, 0);

  return {
    slices: slices.map((s) => ({
      ...s,
      percent: total > 0 ? Math.round((s.points / total) * 100) : 0,
    })),
    total,
  };
};

/** Map GetLoyaltySummary into dashboard-friendly shape. */
export const parseLoyaltySummary = (payload) => {
  const data = unwrapData({ data: payload }) ?? payload ?? {};
  const total = toNum(
    data.totalLoyaltyPoints ??
      data.TotalLoyaltyPoints ??
      data.totalPointsEarned ??
      data.TotalPointsEarned
  );
  const available = toNum(
    data.availableLoyaltyPoints ??
      data.AvailableLoyaltyPoints ??
      data.availablePoints ??
      data.AvailablePoints
  );
  const events = normalizeLoyaltyEvents(data.events || data.Events || []);
  const tiers = normalizeLoyaltyTiers(data.tiers || data.Tiers || []);
  const recent = normalizeLoyaltyHistory({ history: data.recentHistory || data.RecentHistory || [] }).rows;

  const currentTier = tiers[0] || null;
  const nextTier = tiers.length > 1 ? tiers[1] : null;

  return {
    totalLoyaltyPoints: total,
    availableLoyaltyPoints: available,
    loyaltyPoints: available,
    availablePoints: available,
    totalPointsEarned: total,
    pointsRedeemed: Math.max(0, total - available),
    pointsEarnedThisMonth: recent
      .filter((r) => {
        if (r.points <= 0 || !r.createdDateTime) return false;
        const d = new Date(r.createdDateTime);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((sum, r) => sum + r.points, 0),
    expiringSoon: toNum(data.expiringSoon),
    tierName: currentTier?.name || pick(data, 'tierName', 'currentTierName') || '—',
    nextTierName: nextTier?.name || '',
    nextTierPoints: nextTier?.required || 0,
    events,
    breakdown: events.map((e) => ({ name: e.name, points: e.points })),
    tiers,
    recentHistory: recent,
    raw: data,
  };
};
